"use client";

import { c as _c } from "react/compiler-runtime";
import { CSSProperties, useRef } from "react";
import { Container, ServiceDeckCard } from "@/components";
import { ServicesData } from "@/data/ServicesData";
import { useIsDesktop } from "@/hooks/ui/useIsDesktop";
import { useSectionProgress } from "@/hooks/animations/useSectionProgress";

// Ground, engagement length and lead team per service, keyed by slug. Presentation
// only, so it stays out of ServicesData.
const DECK_META: Record<string, {
  tone: string;
  duration: string;
  lead: string;
  tools: string[];
}> = {
  "brand-identity": {
    tone: "var(--color-primary)",
    duration: "4–6 weeks",
    lead: "Strategy & Design",
    tools: ["Figma", "Illustrator", "After Effects", "Brand Storybook"]
  },
  "saas-application": {
    tone: "#E8E4D8",
    duration: "12+ weeks",
    lead: "Product & Engineering",
    tools: ["Next.js", "TypeScript", "PostgreSQL", "Vercel"]
  },
  "web-development": {
    tone: "#F4B1A4",
    duration: "6–10 weeks",
    lead: "Engineering & Performance",
    tools: ["Next.js", "Tailwind", "Headless CMS", "Edge Delivery"]
  },
  "video-editing": {
    tone: "#FFF7D6",
    duration: "2–4 weeks",
    lead: "Motion & Post",
    tools: ["Premiere Pro", "After Effects", "DaVinci", "Blender"]
  }
};
const ServiceDeck = () => {
  const $ = _c(18);
  const sectionRef = useRef(null);
  const isDesktop = useIsDesktop();
  const progress = useSectionProgress(sectionRef, isDesktop);
  const total = ServicesData.length;
  const position = progress * (total - 1);
  const activeIndex = Math.min(total, Math.floor(position) + 1);
  let t0;
  if ($[0] !== isDesktop) {
    t0 = isDesktop ? {
      height: `${total * 55 + 80}vh`
    } as CSSProperties : undefined;
    $[0] = isDesktop;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Container className="flex flex-col gap-5 px-4 py-16 lg:hidden">{ServicesData.map(_temp)}</Container>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== position) {
    t2 = ServicesData.map((service_0, index) => {
      const meta_0 = DECK_META[service_0.slug];
      const offset = position - index;
      const waiting = offset <= 0;
      const eased = Math.min(1, Math.abs(offset));
      const style = {
        zIndex: total - index,
        opacity: waiting ? 1 : 1 - eased,
        pointerEvents: offset > 0.5 ? "none" : "auto",
        transform: waiting ? `translate3d(0, ${eased * 24}%, 0) scale(${1 - eased * 0.05})` : `translate3d(${eased * -13}%, ${eased * -24}%, 0) rotate(${eased * -15}deg) scale(${1 + eased * 0.03})`
      };
      return <div key={service_0.id} style={style} className="absolute inset-0 origin-[50%_130%] shadow-[0_30px_70px_-30px_rgba(0,0,0,0.66)] will-change-transform"><ServiceDeckCard data={service_0} tone={meta_0.tone} duration={meta_0.duration} lead={meta_0.lead} tools={meta_0.tools} /></div>;
    });
    $[3] = position;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <div className="relative hidden h-[min(82vh,680px)] w-[min(94vw,1080px)] lg:block">{t2}</div>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = String(activeIndex);
  let t5;
  if ($[7] !== t4) {
    t5 = t4.padStart(2, "0");
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = String(total).padStart(2, "0");
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <p className="hidden font-mono text-[11px] tracking-[0.26em] text-muted-foreground uppercase lg:absolute lg:bottom-6 lg:left-1/2 lg:block lg:-translate-x-1/2">{t5} /{" "}{t6}</p>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t3 || $[13] !== t7) {
    t8 = <div className="lg:sticky lg:top-0 lg:grid lg:h-screen lg:place-items-center lg:overflow-hidden">{t1}{t3}{t7}</div>;
    $[12] = t3;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t0 || $[16] !== t8) {
    t9 = <section ref={sectionRef} id="service-deck" style={t0} className="relative w-full bg-background">{t8}</section>;
    $[15] = t0;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};
export default ServiceDeck;
function _temp(service) {
  const meta = DECK_META[service.slug];
  return <ServiceDeckCard key={service.id} data={service} tone={meta.tone} duration={meta.duration} lead={meta.lead} tools={meta.tools} />;
}